import { useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	MagnifyingGlassIcon,
	XMarkIcon,
	UsersIcon,
	UserPlusIcon,
	CalendarDaysIcon,
	CurrencyDollarIcon,
	ArrowDownTrayIcon,
	PlusIcon,
	EyeIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AppLayout from '../../components/layout/AppLayout';
import { Modal, Spinner, AppLoader } from '../../components/ui';
import { customersApi, ApiError } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import { downloadCsvString } from '../../utils/csvParser';
import hairChartIcon from '../../assets/hair-chart.png';
import type {
	CustomerListItem,
	CustomerDashboardResponse,
	CreateCustomerRequest,
} from '../../types/customer';

const PAGE_SIZE = 12;

const formatCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatCompactCurrency = (amount: number): string => {
	if (amount >= 1000) return '$' + (amount / 1000).toFixed(1) + 'K';
	return '$' + amount.toFixed(0);
};

const getInitials = (name: string): string => {
	if (!name) return '?';
	const parts = name.trim().split(/\s+/);
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
	return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const AVATAR_COLORS = [
	'#667eea',
	'#764ba2',
	'#f59e0b',
	'#10b981',
	'#ec4899',
	'#3b82f6',
	'#8b5cf6',
	'#06b6d4',
	'#ef4444',
	'#14b8a6',
];

const getAvatarColor = (id: number): string => AVATAR_COLORS[id % AVATAR_COLORS.length];

// ─── Stat Card ──────────────────────────────────────────────────

interface StatCardProps {
	icon: ReactNode;
	iconBg: string;
	value: string | number;
	label: string;
	valueColor?: string;
}

const StatCard = ({ icon, iconBg, value, label, valueColor }: StatCardProps) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
		<div className="flex items-center gap-3">
			<div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
				{icon}
			</div>
			<div className="min-w-0">
				<p
					className={`text-xl font-bold ${valueColor ?? 'text-gray-900 dark:text-white'} leading-tight`}
				>
					{value}
				</p>
				<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
			</div>
		</div>
	</div>
);

// ─── Customer Card ──────────────────────────────────────────────

interface CustomerCardProps {
	customer: CustomerListItem;
	onView: (c: CustomerListItem) => void;
	onHairChart: (c: CustomerListItem) => void;
	hairChartDisabled: boolean;
}

const CustomerCard = ({ customer, onView, onHairChart, hairChartDisabled }: CustomerCardProps) => {
	const color = getAvatarColor(customer.id);
	return (
		<div
			onClick={() => onView(customer)}
			className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer hover:border-[#667eea] hover:shadow-md hover:-translate-y-0.5 transition-all"
		>
			<div className="flex items-center gap-3 mb-3">
				<div
					className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
					style={{ background: color }}
				>
					{getInitials(customer.name)}
				</div>
				<div className="flex-1 min-w-0">
					<p className="font-bold text-gray-900 dark:text-white truncate">
						{customer.name}
					</p>
					<p className="text-xs text-gray-500 dark:text-gray-400 truncate">
						{customer.phoneNumber || customer.email || '—'}
					</p>
				</div>
			</div>
			<div className="flex gap-4 py-3 border-y border-gray-100 dark:border-gray-700 mb-3">
				<div className="flex-1 text-center">
					<p className="text-lg font-bold text-gray-900 dark:text-white leading-none">
						{customer.visitCount}
					</p>
					<p className="text-[11px] text-gray-400 uppercase tracking-wide mt-1">Visits</p>
				</div>
				<div className="flex-1 text-center">
					<p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 leading-none">
						{formatCurrency(customer.totalSpent)}
					</p>
					<p className="text-[11px] text-gray-400 uppercase tracking-wide mt-1">
						Total Spent
					</p>
				</div>
			</div>
			<div className="flex gap-2 justify-end" onClick={e => e.stopPropagation()}>
				<button
					onClick={() => onView(customer)}
					className="cursor-pointer px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1.5"
					title="View Details"
				>
					<EyeIcon className="w-4 h-4" />
					<span>View</span>
				</button>
				<button
					onClick={() => onHairChart(customer)}
					disabled={hairChartDisabled}
					className="cursor-pointer px-3 py-1.5 rounded-lg text-xs font-semibold text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
					title="Open Hair Chart"
				>
					<img src={hairChartIcon} alt="" className="w-4 h-4" />
					<span>Hair Chart</span>
				</button>
			</div>
		</div>
	);
};

// ─── Customer Form Modal (Add/Edit) ─────────────────────────────

interface CustomerFormModalProps {
	isOpen: boolean;
	mode: 'add' | 'edit';
	initial: CustomerListItem | null;
	onClose: () => void;
	onSubmit: (data: CreateCustomerRequest, id: number | null) => Promise<void>;
}

const CustomerFormModal = ({
	isOpen,
	mode,
	initial,
	onClose,
	onSubmit,
}: CustomerFormModalProps) => {
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setName(initial?.name ?? '');
			setEmail(initial?.email ?? '');
			setPhoneNumber(initial?.phoneNumber ?? '');
			setIsSaving(false);
		}
	}, [isOpen, initial]);

	const canSave =
		name.trim().length > 0 && (email.trim().length > 0 || phoneNumber.trim().length > 0);

	const handleSubmit = async () => {
		if (!canSave) return;
		setIsSaving(true);
		try {
			await onSubmit(
				{ name: name.trim(), email: email.trim(), phoneNumber: phoneNumber.trim() },
				initial?.id ?? null
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={mode === 'add' ? 'Add Customer' : 'Edit Customer'}
			size="md"
		>
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
						Name <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={name}
						onChange={e => setName(e.target.value)}
						placeholder="Full name"
						className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
					/>
				</div>
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
						Email
					</label>
					<input
						type="email"
						value={email}
						onChange={e => setEmail(e.target.value)}
						placeholder="email@example.com"
						className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
					/>
				</div>
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
						Phone Number
					</label>
					<input
						type="tel"
						value={phoneNumber}
						onChange={e => setPhoneNumber(e.target.value)}
						placeholder="(555) 123-4567"
						className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
					/>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					At least one of email or phone number is required.
				</p>
				<div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
					<button
						onClick={onClose}
						disabled={isSaving}
						className="cursor-pointer px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Cancel
					</button>
					<button
						onClick={handleSubmit}
						disabled={!canSave || isSaving}
						className="cursor-pointer px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						{isSaving && <Spinner size="sm" />}
						{mode === 'add' ? 'Save Customer' : 'Save Changes'}
					</button>
				</div>
			</div>
		</Modal>
	);
};

// ─── Customer Detail Modal ──────────────────────────────────────

interface CustomerDetailModalProps {
	customer: CustomerListItem | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (c: CustomerListItem) => void;
	onBookAppointment: (c: CustomerListItem) => void;
}

const CustomerDetailModal = ({
	customer,
	isOpen,
	onClose,
	onEdit,
	onBookAppointment,
}: CustomerDetailModalProps) => {
	if (!customer) return null;
	const color = getAvatarColor(customer.id);
	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Customer Details" size="md">
			<div className="space-y-5">
				<div className="flex items-center gap-4">
					<div
						className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
						style={{ background: color }}
					>
						{getInitials(customer.name)}
					</div>
					<div className="min-w-0">
						<h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
							{customer.name}
						</h3>
						{customer.email && (
							<p className="text-sm text-gray-500 dark:text-gray-400 truncate">
								{customer.email}
							</p>
						)}
						{customer.phoneNumber && (
							<p className="text-sm text-gray-500 dark:text-gray-400 truncate">
								{customer.phoneNumber}
							</p>
						)}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<div className="bg-gray-50 dark:bg-gray-700/50 p-3.5 rounded-lg text-center">
						<p className="text-xl font-bold text-gray-900 dark:text-white">
							{customer.visitCount}
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">
							Visits
						</p>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-3.5 rounded-lg text-center">
						<p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
							{formatCurrency(customer.totalSpent)}
						</p>
						<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">
							Total Spent
						</p>
					</div>
				</div>

				<div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
					<button
						onClick={() => onBookAppointment(customer)}
						className="cursor-pointer flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						<CalendarDaysIcon className="w-4 h-4" />
						Book Appointment
					</button>
					<button
						onClick={() => onEdit(customer)}
						className="cursor-pointer px-4 py-2.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-2"
					>
						Edit
					</button>
				</div>
			</div>
		</Modal>
	);
};

// ─── Main Page ──────────────────────────────────────────────────

const Customers = () => {
	const navigate = useNavigate();
	const { profile, selectedLocationId } = useProviderProfileStore();

	const [data, setData] = useState<CustomerDashboardResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [sortBy, setSortBy] = useState<string>('NAME');
	const [page, setPage] = useState(0);

	const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItem | null>(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);

	const [formModal, setFormModal] = useState<{
		isOpen: boolean;
		mode: 'add' | 'edit';
		initial: CustomerListItem | null;
	}>({ isOpen: false, mode: 'add', initial: null });

	const [isExporting, setIsExporting] = useState(false);

	// Debounce search
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => {
			setDebouncedSearch(search);
			setPage(0);
		}, 400);
		return () => {
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, [search]);

	const fetchCustomers = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		setError(null);
		try {
			const response = await customersApi.getDashboard({
				locationId: selectedLocationId,
				search: debouncedSearch || undefined,
				sortBy: sortBy || undefined,
				page,
				size: PAGE_SIZE,
			});
			setData(response);
		} catch (e) {
			const message =
				e instanceof ApiError ? e.message : 'Failed to load customers. Please try again.';
			setError(message);
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, debouncedSearch, sortBy, page]);

	useEffect(() => {
		fetchCustomers();
	}, [fetchCustomers]);

	const hairChartDisabled = !profile?.id || !selectedLocationId;

	const handleHairChart = useCallback(
		(customer: CustomerListItem) => {
			if (!profile?.id || !selectedLocationId) return;
			const baseUrl = import.meta.env.VITE_HAIR_CHART_URL;
			window.open(
				`${baseUrl}/provider/${profile.id}/${customer.id}/${selectedLocationId}`,
				'_blank',
				'noopener,noreferrer'
			);
		},
		[profile?.id, selectedLocationId]
	);

	const handleBookAppointment = useCallback(
		(customer: CustomerListItem) => {
			const params = new URLSearchParams({ action: 'new' });
			if (customer.email) params.append('customerEmail', customer.email);
			else if (customer.phoneNumber) params.append('customerPhone', customer.phoneNumber);
			navigate(`/providers/appointments?${params.toString()}`);
		},
		[navigate]
	);

	const handleView = (customer: CustomerListItem) => {
		setSelectedCustomer(customer);
		setIsDetailOpen(true);
	};

	const handleOpenAdd = () => {
		setFormModal({ isOpen: true, mode: 'add', initial: null });
	};

	const handleOpenEdit = (customer: CustomerListItem) => {
		setIsDetailOpen(false);
		setFormModal({ isOpen: true, mode: 'edit', initial: customer });
	};

	const handleFormSubmit = async (data: CreateCustomerRequest, id: number | null) => {
		try {
			if (id === null) {
				await customersApi.create(data);
				toast.success('Customer added successfully');
				setFormModal({ isOpen: false, mode: 'add', initial: null });
				fetchCustomers();
			} else {
				await customersApi.update(id, {
					name: data.name,
					phoneNumber: data.phoneNumber,
					preferredLanguage: '',
				});
				toast.success('Customer updated successfully');
				setFormModal({ isOpen: false, mode: 'edit', initial: null });
				fetchCustomers();
			}
		} catch (e) {
			const message = e instanceof ApiError ? e.message : 'Failed to save customer.';
			toast.error(message);
		}
	};

	const handleExport = async () => {
		if (!selectedLocationId || isExporting) return;
		setIsExporting(true);
		try {
			const csv = await customersApi.exportCsv(selectedLocationId);
			const timestamp = new Date().toISOString().slice(0, 10);
			downloadCsvString(csv, `customers-${timestamp}.csv`);
			toast.success('Customers exported');
		} catch (e) {
			const message = e instanceof ApiError ? e.message : 'Failed to export customers.';
			toast.error(message);
		} finally {
			setIsExporting(false);
		}
	};

	const totalPages = useMemo(() => {
		if (!data) return 0;
		return Math.max(1, Math.ceil(data.totalElements / PAGE_SIZE));
	}, [data]);

	const rangeStart = data && data.customers.length > 0 ? page * PAGE_SIZE + 1 : 0;
	const rangeEnd = data ? Math.min((page + 1) * PAGE_SIZE, data.totalElements) : 0;

	const visiblePageButtons = useMemo(() => {
		const buttons: (number | '...')[] = [];
		if (totalPages <= 1) return buttons;
		const current = page + 1;
		for (let i = 1; i <= totalPages; i++) {
			if (i === 1 || i === totalPages || (i >= current - 1 && i <= current + 1)) {
				buttons.push(i);
			} else if (i === current - 2 || i === current + 2) {
				buttons.push('...');
			}
		}
		return buttons;
	}, [page, totalPages]);

	if (!selectedLocationId) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<UsersIcon className="w-16 h-16 text-gray-300 dark:text-gray-600" />
					<p className="text-gray-500 dark:text-gray-400">
						Select a location to view customers
					</p>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
						<UsersIcon className="w-7 h-7 text-[#667eea]" />
						Customer List
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						Manage your customers across this location
					</p>
				</div>
				<div className="flex gap-2">
					<button
						onClick={handleExport}
						disabled={isExporting}
						className="cursor-pointer px-4 py-2.5 rounded-lg text-sm font-semibold bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isExporting ? (
							<Spinner size="sm" />
						) : (
							<ArrowDownTrayIcon className="w-4 h-4" />
						)}
						Export
					</button>
					<button
						onClick={handleOpenAdd}
						className="cursor-pointer px-4 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center gap-2"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						<PlusIcon className="w-4 h-4" />
						Add Customer
					</button>
				</div>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
				<StatCard
					icon={<UsersIcon className="w-5 h-5 text-white" />}
					iconBg="bg-gradient-to-br from-[#667eea] to-[#764ba2]"
					value={data?.totalCustomers ?? 0}
					label="Total Customers"
				/>
				<StatCard
					icon={<UserPlusIcon className="w-5 h-5 text-white" />}
					iconBg="bg-emerald-500"
					value={data?.activeCount ?? 0}
					label="Active (90 days)"
					valueColor="text-emerald-600 dark:text-emerald-400"
				/>
				<StatCard
					icon={<CalendarDaysIcon className="w-5 h-5 text-white" />}
					iconBg="bg-amber-500"
					value={data?.newThisMonth ?? 0}
					label="New This Month"
					valueColor="text-amber-600 dark:text-amber-400"
				/>
				<StatCard
					icon={<CurrencyDollarIcon className="w-5 h-5 text-white" />}
					iconBg="bg-blue-500"
					value={formatCompactCurrency(data?.totalRevenue ?? 0)}
					label="Total Revenue"
					valueColor="text-blue-600 dark:text-blue-400"
				/>
			</div>

			{/* Filters */}
			<div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-5">
				<div className="flex flex-col sm:flex-row gap-3">
					<div className="relative flex-1 min-w-[200px]">
						<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							placeholder="Search customers..."
							value={search}
							onChange={e => setSearch(e.target.value)}
							className="w-full pl-9 pr-16 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
						/>
						<div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
							{isLoading && data && <Spinner size="sm" />}
							{search && (
								<button
									onClick={() => setSearch('')}
									className="cursor-pointer p-1 text-gray-400 hover:text-gray-600"
								>
									<XMarkIcon className="w-4 h-4" />
								</button>
							)}
						</div>
					</div>
					<select
						value={sortBy}
						onChange={e => {
							setSortBy(e.target.value);
							setPage(0);
						}}
						className="px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 min-w-[150px]"
					>
						<option value="NAME">Sort by Name</option>
						<option value="SPENT">Sort by Spent</option>
					</select>
				</div>
			</div>

			{/* Content */}
			{isLoading && !data ? (
				<AppLoader message="Loading customers" variant="inline" />
			) : error ? (
				<div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700">
					<p className="text-red-500 dark:text-red-400 mb-3">{error}</p>
					<button
						onClick={fetchCustomers}
						className="cursor-pointer px-4 py-2 rounded-lg text-sm font-semibold text-white"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						Retry
					</button>
				</div>
			) : !data || data.customers.length === 0 ? (
				<div className="bg-white dark:bg-gray-800 rounded-xl py-16 text-center border border-gray-200 dark:border-gray-700">
					<UsersIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
						No customers found
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						{debouncedSearch
							? 'Try adjusting your search'
							: 'Add your first customer to get started'}
					</p>
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-5">
						{data.customers.map(customer => (
							<CustomerCard
								key={customer.id}
								customer={customer}
								onView={handleView}
								onHairChart={handleHairChart}
								hairChartDisabled={hairChartDisabled}
							/>
						))}
					</div>

					{/* Pagination */}
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3">
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Showing {rangeStart}-{rangeEnd} of {data.totalElements} customers
						</p>
						<div className="flex gap-1.5 flex-wrap">
							<button
								onClick={() => setPage(p => Math.max(0, p - 1))}
								disabled={page === 0}
								className="cursor-pointer w-9 h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700"
							>
								<ChevronLeftIcon className="w-4 h-4" />
							</button>
							{visiblePageButtons.map((btn, idx) =>
								btn === '...' ? (
									<span
										key={`ellipsis-${idx}`}
										className="w-9 h-9 flex items-center justify-center text-gray-400 text-sm"
									>
										…
									</span>
								) : (
									<button
										key={btn}
										onClick={() => setPage(btn - 1)}
										className={`cursor-pointer min-w-[36px] h-9 px-2 rounded-md border text-sm font-medium flex items-center justify-center ${
											btn - 1 === page
												? 'text-white border-transparent'
												: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-[#667eea] hover:text-[#667eea]'
										}`}
										style={
											btn - 1 === page
												? {
														background:
															'linear-gradient(135deg, #667eea, #764ba2)',
													}
												: undefined
										}
									>
										{btn}
									</button>
								)
							)}
							<button
								onClick={() => setPage(p => p + 1)}
								disabled={page + 1 >= totalPages}
								className="cursor-pointer w-9 h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 flex items-center justify-center hover:border-[#667eea] hover:text-[#667eea] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700"
							>
								<ChevronRightIcon className="w-4 h-4" />
							</button>
						</div>
					</div>
				</>
			)}

			{/* Modals */}
			<CustomerDetailModal
				customer={selectedCustomer}
				isOpen={isDetailOpen}
				onClose={() => setIsDetailOpen(false)}
				onEdit={handleOpenEdit}
				onBookAppointment={handleBookAppointment}
			/>
			<CustomerFormModal
				isOpen={formModal.isOpen}
				mode={formModal.mode}
				initial={formModal.initial}
				onClose={() => setFormModal({ isOpen: false, mode: 'add', initial: null })}
				onSubmit={handleFormSubmit}
			/>
		</AppLayout>
	);
};

export default Customers;
